<template>
  <Teleport to="body">
    <TransitionRoot appear :show="modal.isOpen.value" as="template">
      <Dialog as="div" @close="modal.close()" class="relative z-[9999]">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0">
          <div class="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-hidden !z-[10000]">
          <div class="flex items-center justify-center min-h-full p-4 text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95">
              <DialogPanel
                class="w-full max-w-[90vw] h-[85vh] transform overflow-hidden rounded-2xl bg-black border border-gray-800 shadow-2xl transition-all flex flex-col">
                <div v-if="camera" class="relative h-full w-full bg-black flex flex-col group">
                  <!-- Header bar -->
                  <div
                    class="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-3 py-1.5 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
                    <div class="flex items-center gap-2 pointer-events-auto">
                      <h3 class="font-bold text-white text-sm drop-shadow-md">{{ camera.cameraName }}</h3>
                      <!-- Stream health dot -->
                      <div
                        :class="['w-2 h-2 rounded-full transition-colors duration-500', streamHealthColor]"
                        :title="
                          streamHealth === 'ok'
                            ? tt('Stream healthy', 'Pretok deluje')
                            : streamHealth === 'no-producer'
                              ? tt('Stream not connected', 'Pretok ni povezan')
                              : tt('Stream error', 'Napaka pretoka')
                        "></div>
                    </div>
                    <div class="flex items-center gap-2 pointer-events-auto">
                      <!-- Reconnect stream button -->
                      <button
                        v-if="streamHealth !== 'ok'"
                        @click="reconnectStream"
                        class="bg-black/50 hover:bg-yellow-600/80 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 w-9"
                        :title="tt('Reconnect stream', 'Ponovno poveži pretok')">
                        <span class="material-symbols-outlined text-base">refresh</span>
                      </button>
                      <!-- PTZ / digital zoom mode toggle -->
                      <button
                        v-if="cameraHasPtz"
                        @click="togglePtzMode"
                        class="bg-black/50 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 w-9"
                        :class="{ 'border-blue-400/50 bg-blue-600/30': !isPtzMode }"
                        :title="
                          isPtzMode
                            ? tt(
                                'PTZ mouse active — click to switch to digital zoom',
                                'PTZ z miško je aktiven — klikni za preklop na digitalno povečavo',
                              )
                            : tt(
                                'Digital zoom active — click to switch to PTZ mouse',
                                'Digitalna povečava je aktivna — klikni za preklop na PTZ z miško',
                              )
                        ">
                        <span class="material-symbols-outlined text-base">{{
                          isPtzMode ? 'pan_tool' : 'zoom_in'
                        }}</span>
                      </button>
                      <!-- Audio mute/unmute button -->
                      <button
                        v-if="streamHasAudio"
                        @click="isMuted = !isMuted"
                        class="bg-black/50 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 w-9"
                        :title="isMuted ? tt('Unmute', 'Vklopi zvok') : tt('Mute', 'Izklopi zvok')">
                        <span class="material-symbols-outlined text-base">{{
                          isMuted ? 'volume_off' : 'volume_up'
                        }}</span>
                      </button>
                      <!-- Stream quality toggle (SD sub stream / HD main stream) -->
                      <button
                        v-if="subStreamAvailable"
                        @click="streamQuality = streamQuality === 'sub' ? 'main' : 'sub'"
                        class="bg-black/50 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 px-3 gap-1"
                        :class="{ 'border-blue-400/50 bg-blue-600/30': streamQuality === 'main' }"
                        :title="
                          streamQuality === 'sub'
                            ? tt('Switch to HD (main stream)', 'Preklopi na HD (glavni pretok)')
                            : tt('Switch back to SD (sub stream)', 'Preklopi nazaj na SD (podpretok)')
                        ">
                        <span class="text-xs font-semibold">{{ streamQuality === 'sub' ? 'HD' : 'SD' }}</span>
                      </button>
                      <!-- Snapshot button -->
                      <button
                        @click="takeSnapshot"
                        :disabled="takingSnapshot"
                        class="bg-black/50 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 w-9 disabled:opacity-50"
                        :title="tt('Take snapshot', 'Zajemi posnetek')">
                        <span
                          v-if="takingSnapshot"
                          class="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></span>
                        <span v-else class="material-symbols-outlined text-base">photo_camera</span>
                      </button>
                      <!-- PiP button -->
                      <button
                        @click="openPiP"
                        class="bg-black/50 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 w-9"
                        :title="tt('Picture in Picture', 'Slika v sliki')">
                        <span class="material-symbols-outlined text-base">picture_in_picture_alt</span>
                      </button>
                      <!-- Close button -->
                      <button
                        @click="modal.close()"
                        class="bg-black/50 hover:bg-red-600/90 text-white rounded-full backdrop-blur-md transition-colors border border-white/10 flex items-center justify-center h-9 w-9"
                        :title="tt('Close', 'Zapri')">
                        <span class="material-symbols-outlined text-base">close</span>
                      </button>
                    </div>
                  </div>

                  <div class="flex-1 relative h-full flex flex-row overflow-hidden">
                    <!-- Video area -->
                    <div
                      ref="videoWrapperRef"
                      class="flex-1 relative h-full bg-black flex flex-col overflow-hidden select-none"
                      :class="[
                        !playingRecording ? 'touch-none' : '',
                        !playingRecording && cameraHasPtz && isPtzMode
                          ? ptzDragging
                            ? 'cursor-grabbing'
                            : 'cursor-grab'
                          : '',
                        !playingRecording && isDigitalZoomMode && zoomPan.scale.value > 1
                          ? 'cursor-grab active:cursor-grabbing'
                          : '',
                      ]"
                      @mouseenter="isHovering = true"
                      @mouseleave="isHovering = false"
                      @mousedown="!playingRecording ? onVideoMouseDown($event) : undefined"
                      @wheel.prevent="!playingRecording ? onVideoWheel($event) : undefined"
                      @dblclick="!playingRecording ? onVideoDoubleClick() : undefined"
                      @touchstart="onVideoTouchStart($event)"
                      @touchmove="onVideoTouchMove($event)"
                      @touchend="onVideoTouchEnd($event)"
                      @touchcancel="onVideoTouchCancel">
                      <div
                        v-show="!playingRecording"
                        class="absolute inset-0"
                        :style="
                          isDigitalZoomMode
                            ? { transform: zoomPan.transformStyle.value, transformOrigin: '0 0' }
                            : undefined
                        ">
                        <WebRTCPlayer
                          ref="webrtcPlayer"
                          :camera-id="camera.cameraId"
                          :poster-src="cameraPosterSnapshot"
                          :stream-preference="streamQuality === 'sub' ? 'prefer-sub' : 'main'"
                          :auto-connect="true"
                          :muted="isMuted"
                          :controls="false"
                          :show-status="false"
                          :show-call="true"
                          class="w-full h-full"
                          @error="loadingStream = false"
                          @playing="loadingStream = false"
                          @audio-available="
                            (has) => {
                              streamHasAudio = has;
                            }
                          " />
                      </div>

                      <!-- Click-to-center crosshair flash -->
                      <Transition name="click-flash">
                        <div
                          v-if="clickFlash"
                          class="absolute pointer-events-none z-40"
                          :style="{
                            left: clickFlash.x + 'px',
                            top: clickFlash.y + 'px',
                            transform: 'translate(-50%, -50%)',
                          }">
                          <div class="w-8 h-8 border-2 border-white rounded-full animate-ping opacity-80"></div>
                          <div class="absolute inset-0 flex items-center justify-center">
                            <div class="w-1 h-1 bg-white rounded-full"></div>
                          </div>
                        </div>
                      </Transition>

                      <!-- Digital zoom reset hint -->
                      <div
                        v-if="isDigitalZoomMode && zoomPan.scale.value > 1"
                        class="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                        <div
                          class="flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-full border border-white/10 text-[10px] text-white/70">
                          <span class="material-symbols-outlined text-xs">zoom_out_map</span>
                          {{
                            isCoarsePointer
                              ? tt('Double-tap to reset zoom', 'Dvakrat tapni za ponastavitev povečave')
                              : tt('Double-click to reset zoom', 'Dvojni klik za ponastavitev povečave')
                          }}
                        </div>
                      </div>

                      <!-- Touch gestures available in PTZ mode (phones/tablets) -->
                      <div
                        v-if="isCoarsePointer && !playingRecording && cameraHasPtz && isPtzMode"
                        class="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                        <div
                          class="flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-full border border-white/10 text-[10px] text-white/70 whitespace-nowrap">
                          <span class="material-symbols-outlined text-xs">touch_app</span>
                          {{
                            tt(
                              'Drag to pan · Pinch to zoom · Tap to centre',
                              'Povleci za pomik · Stisni za povečavo · Tapni za sredino',
                            )
                          }}
                        </div>
                      </div>

                      <!-- Recording playback overlay -->
                      <div v-if="playingRecording" class="absolute inset-0 bg-black z-20 flex flex-col">
                        <video
                          ref="recordingVideo"
                          :src="recordingVideoUrl"
                          class="flex-1 w-full h-full object-contain"
                          controls
                          autoplay
                          @ended="stopPlayback" />
                        <!-- Recording info bar -->
                        <div class="absolute top-14 left-4 z-30 flex items-center gap-2">
                          <button
                            @click="stopPlayback"
                            class="flex items-center gap-1.5 px-3 py-1.5 bg-black/70 hover:bg-black/90 text-white text-xs rounded-full backdrop-blur-md border border-white/10 transition-all shadow-lg">
                            <span class="material-symbols-outlined text-sm">live_tv</span>
                            {{ tt('Back to live', 'Nazaj v živo') }}
                          </button>
                          <span
                            v-if="playingRecordingInfo"
                            class="text-[10px] text-white/70 bg-black/50 px-2 py-1 rounded-full backdrop-blur-sm">
                            {{ playingRecordingInfo }}
                          </span>
                        </div>
                      </div>

                      <div
                        v-if="loadingStream && !playingRecording"
                        class="absolute inset-0 flex items-center justify-center bg-black/40 z-10 backdrop-blur-[1px]">
                        <div class="flex flex-col items-center gap-2">
                          <div class="w-6 h-6 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
                          <span class="text-xs text-white/80 font-medium tracking-wide">{{
                            tt('Connecting...', 'Povezovanje...')
                          }}</span>
                        </div>
                      </div>

                      <!-- Motion badge -->
                      <div
                        v-if="motionActive"
                        class="absolute top-14 left-4 z-30 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-600/90 backdrop-blur-md border border-red-500/50 shadow-lg animate-pulse">
                        <span class="material-symbols-outlined text-sm text-white">motion_sensor_active</span>
                        <span class="text-[10px] font-bold tracking-widest text-white">{{
                          tt('MOTION', 'GIBANJE')
                        }}</span>
                      </div>

                      <!-- Sidebar toggle buttons -->
                      <div
                        class="absolute top-16 right-3 z-30 flex flex-col gap-2 transition-all duration-300"
                        :class="showControls ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'">
                        <button
                          v-if="cameraHasPtz"
                          @click.stop="toggleSidebar('ptz')"
                          class="w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/80 text-white rounded-full backdrop-blur-md border border-white/10 transition-all shadow-lg active:scale-95"
                          :class="{
                            'bg-blue-600/90 hover:bg-blue-600 border-blue-500/50 ring-2 ring-blue-500/30':
                              activeTab === 'ptz' && isSidebarOpen,
                          }"
                          :title="tt('PTZ Controls', 'Upravljanje PTZ')">
                          <span class="material-symbols-outlined text-sm">control_camera</span>
                        </button>

                        <button
                          @click.stop="toggleSidebar('recordings')"
                          class="w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/80 text-white rounded-full backdrop-blur-md border border-white/10 transition-all shadow-lg active:scale-95"
                          :class="{
                            'bg-blue-600/90 hover:bg-blue-600 border-blue-500/50 ring-2 ring-blue-500/30':
                              activeTab === 'recordings' && isSidebarOpen,
                          }"
                          :title="tt('Recordings', 'Posnetki')">
                          <span class="material-symbols-outlined text-sm">video_library</span>
                        </button>

                        <button
                          @click.stop="toggleSidebar('events')"
                          class="w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/80 text-white rounded-full backdrop-blur-md border border-white/10 transition-all shadow-lg active:scale-95"
                          :class="{
                            'bg-blue-600/90 hover:bg-blue-600 border-blue-500/50 ring-2 ring-blue-500/30':
                              activeTab === 'events' && isSidebarOpen,
                          }"
                          :title="tt('Motion Events', 'Dogodki gibanja')">
                          <span class="material-symbols-outlined text-sm">sensors</span>
                        </button>
                      </div>
                    </div>

                    <!-- Sidebar -->
                    <div
                      class="bg-gray-900 border-l border-gray-800 flex flex-col h-full transition-all duration-300 ease-in-out z-20 shrink-0 overflow-hidden"
                      :class="isSidebarOpen ? 'w-[280px] opacity-100' : 'w-0 opacity-0'">
                      <div class="px-4 py-3 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
                        <h4 class="text-gray-200 font-semibold text-xs uppercase tracking-wider">
                          {{ sidebarTitle }}
                        </h4>
                        <button
                          @click="isSidebarOpen = false"
                          class="text-gray-500 hover:text-white transition-colors p-1">
                          <span class="material-symbols-outlined text-sm">close</span>
                        </button>
                      </div>

                      <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 relative custom-scrollbar bg-gray-900">
                        <!-- PTZ Tab -->
                        <template v-if="activeTab === 'ptz'">
                          <div class="flex flex-col items-center justify-start h-full gap-6 pt-4">
                            <div class="relative">
                              <PTZJoystick @update="handlePTZ" />
                              <div
                                class="absolute inset-0 border border-white/5 rounded-full -m-4 pointer-events-none animate-pulse-slow"></div>
                            </div>
                            <div class="text-center space-y-1">
                              <p class="text-[10px] text-gray-500 leading-relaxed px-4">
                                {{
                                  tt(
                                    'Drag to pan/tilt. Scroll or pinch to zoom.',
                                    'Povleci za pomik/nagib. Zavrti ali stisni za povečavo.',
                                  )
                                }}
                              </p>
                            </div>

                            <!-- Presets section -->
                            <div class="w-full pt-4 border-t border-gray-800/50">
                              <div class="flex items-center justify-between mb-3">
                                <p class="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                                  {{ tt('Presets', 'Prednastavitve') }}
                                </p>
                                <button
                                  @click="showSavePreset = !showSavePreset"
                                  class="text-gray-500 hover:text-blue-400 transition-colors"
                                  :title="
                                    tt('Save current position as preset', 'Shrani trenutni položaj kot prednastavitev')
                                  ">
                                  <span class="material-symbols-outlined text-sm">add_circle</span>
                                </button>
                              </div>

                              <!-- Save preset form -->
                              <div v-if="showSavePreset" class="mb-3 flex gap-2">
                                <input
                                  v-model="newPresetName"
                                  type="text"
                                  :placeholder="tt('Preset name', 'Ime prednastavitve')"
                                  class="flex-1 bg-gray-800 border border-gray-700 rounded text-xs text-white px-2 py-1.5 placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                                  @keyup.enter="savePreset" />
                                <button
                                  @click="savePreset"
                                  :disabled="!newPresetName.trim() || savingPreset"
                                  class="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded px-2 py-1.5 text-xs transition-colors">
                                  {{ savingPreset ? '...' : tt('Save', 'Shrani') }}
                                </button>
                              </div>

                              <!-- Preset error message -->
                              <div
                                v-if="presetError"
                                class="mb-3 p-2 bg-red-900/30 border border-red-700 rounded text-xs text-red-300">
                                {{ presetError }}
                              </div>

                              <!-- Preset list -->
                              <div v-if="loadingPresets" class="flex justify-center py-4">
                                <div
                                  class="w-4 h-4 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin"></div>
                              </div>
                              <div v-else-if="presets.length === 0" class="text-center py-3">
                                <p class="text-[10px] text-gray-600">
                                  {{ tt('No presets saved', 'Ni shranjenih prednastavitev') }}
                                </p>
                              </div>
                              <div v-else class="space-y-1.5">
                                <div
                                  v-for="preset in presets"
                                  :key="preset.presetToken"
                                  class="flex items-center gap-2 group/preset">
                                  <button
                                    @click="gotoPreset(preset)"
                                    :disabled="goingToPreset === preset.presetToken"
                                    class="flex-1 flex items-center gap-2 py-2 px-2.5 bg-gray-800/50 hover:bg-gray-800 border border-transparent hover:border-gray-700/50 text-gray-400 hover:text-gray-200 rounded text-xs transition-all text-left disabled:opacity-50">
                                    <span
                                      class="material-symbols-outlined text-sm"
                                      :class="preset.isHome ? 'text-yellow-500' : 'text-gray-600'">
                                      {{ preset.isHome ? 'home' : 'photo_camera' }}
                                    </span>
                                    <span class="truncate flex-1">{{ preset.presetName }}</span>
                                    <span
                                      v-if="goingToPreset === preset.presetToken"
                                      class="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                                  </button>
                                  <button
                                    @click="deletePreset(preset)"
                                    class="opacity-0 group-hover/preset:opacity-100 text-gray-600 hover:text-red-400 transition-all p-1"
                                    :title="tt('Delete preset', 'Izbriši prednastavitev')">
                                    <span class="material-symbols-outlined text-sm">delete</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <!-- Recordings Tab -->
                        <template v-if="activeTab === 'recordings'">
                          <div class="flex flex-col gap-3">
                            <!-- Date filter -->
                            <div class="flex gap-2">
                              <input
                                v-model="recordingsFilter.startDate"
                                type="date"
                                class="flex-1 bg-gray-800 border border-gray-700 rounded text-xs text-white px-2 py-1.5 focus:border-blue-500 focus:outline-none" />
                              <input
                                v-model="recordingsFilter.endDate"
                                type="date"
                                class="flex-1 bg-gray-800 border border-gray-700 rounded text-xs text-white px-2 py-1.5 focus:border-blue-500 focus:outline-none" />
                            </div>
                            <button
                              @click="loadRecordings"
                              :disabled="loadingRecordings"
                              class="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs py-1.5 rounded transition-colors disabled:opacity-50">
                              {{ loadingRecordings ? tt('Loading...', 'Nalaganje...') : tt('Filter', 'Filtriraj') }}
                            </button>

                            <!-- Recording list -->
                            <div v-if="loadingRecordings" class="flex justify-center py-8">
                              <div
                                class="w-5 h-5 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin"></div>
                            </div>
                            <div
                              v-else-if="recordings.length === 0"
                              class="flex flex-col items-center justify-center py-8 text-gray-500 space-y-3">
                              <div class="w-12 h-12 rounded-full bg-gray-800/50 flex items-center justify-center">
                                <span class="material-symbols-outlined text-xl opacity-50">movie</span>
                              </div>
                              <p class="text-xs text-gray-400">
                                {{ tt('No recordings found', 'Ni najdenih posnetkov') }}
                              </p>
                            </div>
                            <div v-else class="space-y-2">
                              <div
                                v-for="recording in recordings"
                                :key="`${recording.recordingEventId}_${recording.segmentNumber}`"
                                class="bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700/30 hover:border-gray-600/50 transition-colors">
                                <img
                                  v-if="thumbnailUrls[getRecordingKey(recording)]"
                                  :src="thumbnailUrls[getRecordingKey(recording)]"
                                  :alt="tt('Recording thumbnail', 'Sličica posnetka')"
                                  class="w-full h-24 object-cover bg-black" />
                                <div v-else class="w-full h-24 bg-black flex items-center justify-center text-gray-500">
                                  <div class="flex flex-col items-center gap-1">
                                    <span class="material-symbols-outlined text-lg opacity-60">
                                      {{ thumbnailLoading[getRecordingKey(recording)] ? 'progress_activity' : 'movie' }}
                                    </span>
                                    <span class="text-[10px]">
                                      {{
                                        thumbnailLoading[getRecordingKey(recording)]
                                          ? tt('Loading thumbnail...', 'Nalaganje sličice...')
                                          : tt('No thumbnail', 'Ni sličice')
                                      }}
                                    </span>
                                  </div>
                                </div>
                                <div class="p-2">
                                  <p class="text-xs text-gray-300 font-medium">
                                    {{ formatDateTime(recording.startTime) }}
                                  </p>
                                  <p class="text-[10px] text-gray-500 mt-0.5">
                                    {{ formatDuration(recording.durationSeconds) }}
                                  </p>
                                  <div class="flex gap-1.5 mt-2">
                                    <button
                                      @click="playRecording(recording)"
                                      class="flex-1 flex items-center justify-center gap-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 text-[10px] py-1 rounded transition-colors">
                                      <span class="material-symbols-outlined text-xs">play_arrow</span>
                                      {{ tt('Play', 'Predvajaj') }}
                                    </button>
                                    <button
                                      @click="downloadRecording(recording)"
                                      class="flex items-center justify-center bg-gray-700/40 hover:bg-gray-600/60 text-gray-400 text-[10px] px-2 py-1 rounded transition-colors"
                                      :title="tt('Download', 'Prenesi')">
                                      <span class="material-symbols-outlined text-xs">download</span>
                                    </button>
                                    <button
                                      @click="deleteRecording(recording)"
                                      class="flex items-center justify-center bg-red-600/10 hover:bg-red-600/30 text-red-400 text-[10px] px-2 py-1 rounded transition-colors">
                                      <span class="material-symbols-outlined text-xs">delete</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <!-- Events Tab -->
                        <template v-if="activeTab === 'events'">
                          <div class="flex flex-col gap-3">
                            <div
                              v-if="motionEvents.length === 0"
                              class="flex flex-col items-center justify-center py-8 text-gray-500 space-y-3">
                              <div class="w-12 h-12 rounded-full bg-gray-800/50 flex items-center justify-center">
                                <span class="material-symbols-outlined text-xl opacity-50">sensors</span>
                              </div>
                              <p class="text-xs text-gray-400">{{ tt('No motion events', 'Ni dogodkov gibanja') }}</p>
                              <p class="text-[10px] text-gray-600">
                                {{
                                  tt('Events appear here in real-time', 'Dogodki se prikazujejo tukaj v realnem času')
                                }}
                              </p>
                            </div>
                            <div v-else class="space-y-2">
                              <div
                                v-for="event in motionEvents"
                                :key="event.id"
                                class="flex items-start gap-2.5 p-2.5 bg-gray-800/40 rounded-lg border border-gray-700/30">
                                <div
                                  class="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                                  :class="event.active ? 'bg-red-600/20 text-red-400' : 'bg-gray-700/50 text-gray-500'">
                                  <span class="material-symbols-outlined text-sm">{{
                                    event.active ? 'motion_sensor_active' : 'motion_sensor_idle'
                                  }}</span>
                                </div>
                                <div class="flex-1 min-w-0">
                                  <p
                                    class="text-xs font-medium"
                                    :class="event.active ? 'text-red-400' : 'text-gray-300'">
                                    {{
                                      event.active
                                        ? tt('Motion Detected', 'Zaznano gibanje')
                                        : tt('Motion Ended', 'Gibanje končano')
                                    }}
                                  </p>
                                  <p class="text-[10px] text-gray-500 mt-0.5">{{ formatTime(event.timestamp) }}</p>
                                </div>
                              </div>
                            </div>
                            <button
                              v-if="motionEvents.length > 0"
                              @click="motionEvents = []"
                              class="text-[10px] text-gray-600 hover:text-gray-400 transition-colors text-center py-1">
                              {{ tt('Clear events', 'Počisti dogodke') }}
                            </button>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { formatDateTime as formatDateTimeLocale, formatTime as formatTimeLocale } from '../utils/formatDate';
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue';
import WebRTCPlayer from './WebRTCPlayer.vue';
import PTZJoystick from './PTZJoystick.vue';
import api from '../services/api';
import { useAuthStore } from '../stores/auth';
import { useCameraModal } from '../composables/useCameraModal';
import { useCameraSnapshotsStore } from '../stores/cameraSnapshots';
import { useSocketStore } from '../stores/socket';
import { useCameraPlayer } from '../composables/useCameraPlayer';
import { useCamerasStore } from '../stores/cameras';
import { useZoomPan } from '../composables/useZoomPan';
import { isTap, pinchTransform, ptzPanVelocity, ptzZoomVelocity, touchPair } from '../utils/cameraGestures';
import { useI18n } from 'vue-i18n';
import { useMessageBox } from '../composables/useMessageBox';
import { tt } from '../utils/consoleText';

const { t } = useI18n();
const { confirm: confirmBox } = useMessageBox();

const modal = useCameraModal();
const authStore = useAuthStore();
const socketStore = useSocketStore();
const cameraPlayer = useCameraPlayer();
const camerasStore = useCamerasStore();
const cameraSnapshotsStore = useCameraSnapshotsStore();

// Video wrapper ref — must be declared before useZoomPan uses it
const videoWrapperRef = ref<HTMLElement | null>(null);

/** Digital zoom ceiling, shared by the wheel/pinch handlers. */
const DIGITAL_ZOOM_MAX = 8;

// Digital zoom via useZoomPan (applied to the video element when in digital zoom mode).
// Pan/zoom input is driven by this component's own handlers (mouse below, touch at
// the bottom of this section) so both input families follow the same mode rules;
// the composable is kept purely as the transform/clamping state holder.
const zoomPan = useZoomPan(videoWrapperRef, {
  enableWheel: false,
  enablePan: true,
  enablePinch: false,
  enableTouchPan: false,
  manageTouchAction: false,
  panOnLeftClick: false,
  minScale: 1,
  maxScale: DIGITAL_ZOOM_MAX,
});

/** True on phones/tablets — where a pinch replaces the wheel and a tap the click. */
const isCoarsePointer = ref(
  typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer: coarse)').matches === true || (navigator?.maxTouchPoints ?? 0) > 0),
);

// -- Local state --
const isSidebarOpen = ref(false);
const activeTab = ref<'ptz' | 'recordings' | 'events'>('ptz');
const isHovering = ref(false);
const loadingStream = ref(false);
const streamQuality = ref<'sub' | 'main'>('sub');
const subStreamAvailable = ref(false);
const isMuted = ref(true);
const streamHasAudio = ref(false);
const webrtcPlayer = ref<InstanceType<typeof WebRTCPlayer> | null>(null);

// PTZ state
let ptzThrottleTimer: ReturnType<typeof setTimeout> | null = null;
let ptzNextData: { x: number; y: number; zoom: number } | null = null;

/** PTZ move body (`handlePTZ` and its throttled follow-ups send the same shape). */
interface PtzMovePayload {
  x: number;
  y: number;
  zoom: number;
}

/** One PTZ preset from the camera API. */
interface PtzPreset {
  presetToken: string;
  presetName?: string;
  isHome?: boolean;
}

/** One recording segment row from the camera API. */
interface CameraRecording {
  cameraId: string;
  recordingEventId: string;
  segmentNumber: number;
  startTime: string;
  durationSeconds: number;
}

/** `GET /cameras/:id/go2rtc/endpoints` payload (sub-stream availability). */
interface SubStreamResponse {
  endpoints?: { whepSubUrl?: string };
}
const isPTZMoving = ref(false);

// Snapshot state
const takingSnapshot = ref(false);

// Mouse PTZ state
const isPtzMode = ref(true); // true = PTZ mouse; false = digital zoom
const ptzDragStart = ref<{ x: number; y: number; clientX: number; clientY: number } | null>(null);
const ptzDragging = ref(false);
let ptzDragThrottleTimer: ReturnType<typeof setTimeout> | null = null;
let ptzZoomStopTimer: ReturnType<typeof setTimeout> | null = null;

// Touch gesture state — one finger pans, two fingers zoom. The zoom is the
// hardware PTZ zoom in PTZ mode and the digital zoom otherwise, so a phone
// behaves exactly like the mouse: drag = pan, pinch = wheel.
type TouchGestureKind = 'none' | 'ptz-drag' | 'ptz-pinch' | 'digital-drag' | 'digital-pinch';
const touchGesture = {
  kind: 'none' as TouchGestureKind,
  startX: 0,
  startY: 0,
  startAt: 0,
  /** PTZ drag: last position actually sent (the throttle anchor). */
  anchor: { clientX: 0, clientY: 0 },
  /** Digital drag: offsets when the drag started. */
  panStartOffsetX: 0,
  panStartOffsetY: 0,
  /** Pinch bookkeeping. */
  pinchStartDistance: 0,
  pinchLastRatio: 1,
  pinchStartScale: 1,
  pinchStartOffsetX: 0,
  pinchStartOffsetY: 0,
  pinchStartMidX: 0,
  pinchStartMidY: 0,
  /** Digital mode: double-tap detection. */
  lastTapAt: 0,
};

// Click-to-center crosshair animation
const clickFlash = ref<{ x: number; y: number } | null>(null);

// Stream health state
const streamHealth = ref<'unknown' | 'ok' | 'no-producer' | 'error'>('unknown');
let streamHealthInterval: ReturnType<typeof setInterval> | null = null;

// Presets state
const presets = ref<PtzPreset[]>([]);
const loadingPresets = ref(false);
const showSavePreset = ref(false);
const newPresetName = ref('');
const savingPreset = ref(false);
const goingToPreset = ref<string | null>(null);
const presetError = ref<string | null>(null);

// Recordings state
const recordings = ref<CameraRecording[]>([]);
const loadingRecordings = ref(false);
const recordingsFilter = ref({
  startDate: '',
  endDate: '',
});
const thumbnailUrls = ref<Record<string, string>>({});
const thumbnailLoading = ref<Record<string, boolean>>({});
const thumbnailLoadFailed = ref<Record<string, boolean>>({});

// Recording playback state
const playingRecording = ref(false);
const recordingVideoUrl = ref('');
const recordingVideo = ref<HTMLVideoElement | null>(null);
const playingRecordingInfo = ref('');

// Motion events state
const motionActive = ref(false);
const motionEvents = ref<Array<{ id: string; active: boolean; timestamp: Date }>>([]);

// -- Computed --
const camera = computed(() => modal.current.value);
const cameraPosterSnapshot = computed(() =>
  camera.value ? cameraSnapshotsStore.getSnapshotUrl(camera.value.cameraId) : null,
);

// Check PTZ capability (resolved by CameraWidget or from store)
const cameraHasPtz = computed(() => {
  if (!camera.value) return false;
  // Check the modal state (set at open time from prop/local data)
  const modalHasPtz = camera.value.hasPtz === true;
  // Also check the live store entry (kept fresh via socket camera:updated events).
  // Use OR: either source asserting true is sufficient — stale store false must
  // not hide PTZ controls when the modal was opened with hasPtz=true.
  const storeCamera = camerasStore.getCamera(String(camera.value.cameraId));
  if (storeCamera) {
    return !!(storeCamera.hasPtz || storeCamera.has_ptz) || modalHasPtz;
  }
  return modalHasPtz;
});

// Digital zoom is active when no PTZ, or when user toggled away from PTZ mouse mode
const isDigitalZoomMode = computed(() => !cameraHasPtz.value || !isPtzMode.value);

const streamHealthColor = computed(() => {
  switch (streamHealth.value) {
    case 'ok':
      return 'bg-green-500';
    case 'no-producer':
      return 'bg-yellow-500';
    case 'error':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
});

// Resolve deviceId from store for socket event matching
const cameraDeviceId = computed(() => {
  if (!camera.value) return null;
  const storeCamera = camerasStore.getCamera(String(camera.value.cameraId));
  return storeCamera?.deviceId || null;
});

const showControls = computed(() => isHovering.value || isSidebarOpen.value);

const sidebarTitle = computed(() => {
  switch (activeTab.value) {
    case 'ptz':
      return tt('PTZ Control', 'Upravljanje PTZ');
    case 'recordings':
      return tt('Recordings', 'Posnetki');
    case 'events':
      return tt('Motion Events', 'Dogodki gibanja');
    default:
      return '';
  }
});

/** Whether this camera has a sub stream (grid quality) available. */
async function loadSubStreamAvailability(cameraId: string | number) {
  try {
    const res = await api.get(`/cameras/${cameraId}/go2rtc/endpoints`);
    const data = ((res as { data?: SubStreamResponse }).data || res) as SubStreamResponse;
    subStreamAvailable.value = Boolean(data?.endpoints?.whepSubUrl);
  } catch {
    subStreamAvailable.value = false;
  }
}

// -- Watchers --
watch(
  () => modal.isOpen.value,
  (open) => {
    if (open) {
      loadingStream.value = true;
      if (camera.value?.cameraId) {
        void cameraSnapshotsStore.fetchSnapshot(camera.value.cameraId);
        // Default to the sub stream (less bandwidth); HD toggle switches to main.
        streamQuality.value = 'sub';
        void loadSubStreamAvailability(camera.value.cameraId);
      }
      motionActive.value = false;
      motionEvents.value = [];
      // Default to PTZ mouse mode when camera has PTZ
      isPtzMode.value = true;
      streamHealth.value = 'unknown';
      // Load presets if PTZ
      if (cameraHasPtz.value) {
        loadPresets();
      }
      // Init recordings filter
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      recordingsFilter.value = {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
      // Listen for motion events
      setupMotionListener();
      // Start polling stream health
      pollStreamHealth();
      streamHealthInterval = setInterval(pollStreamHealth, 10_000);
    } else {
      cleanupMotionListener();
      if (streamHealthInterval) {
        clearInterval(streamHealthInterval);
        streamHealthInterval = null;
      }
      isSidebarOpen.value = false;
      isMuted.value = true;
      streamHasAudio.value = false;
      loadingStream.value = false;
      presets.value = [];
      recordings.value = [];
      clearThumbnailUrls();
      showSavePreset.value = false;
      newPresetName.value = '';
      presetError.value = null;
      stopPlayback();
      zoomPan.resetZoom();
      resetTouchGesture();
      removePtzMouseListeners();
    }
  },
);

watch(
  () => camera.value?.cameraId,
  (cameraId, previousCameraId) => {
    if (!modal.isOpen.value || !cameraId || cameraId === previousCameraId) return;

    loadingStream.value = true;
    streamQuality.value = 'sub';
    void loadSubStreamAvailability(cameraId);
    void cameraSnapshotsStore.fetchSnapshot(cameraId);
  },
);

// -- Sidebar --
function toggleSidebar(tab: 'ptz' | 'recordings' | 'events') {
  if (isSidebarOpen.value && activeTab.value === tab) {
    isSidebarOpen.value = false;
  } else {
    activeTab.value = tab;
    isSidebarOpen.value = true;
    // Load data on tab switch
    if (tab === 'recordings' && recordings.value.length === 0) {
      loadRecordings();
    }
  }
}

// -- PiP --
function openPiP() {
  if (!camera.value) return;
  cameraPlayer.open({
    cameraId: camera.value.cameraId,
    cameraName: camera.value.cameraName,
  });
  modal.close();
}

// -- PTZ Mode Toggle --
function togglePtzMode() {
  isPtzMode.value = !isPtzMode.value;
  // The mode changes what a drag and a pinch mean, so no gesture may survive it.
  resetTouchGesture();
  ptzDragging.value = false;
  if (ptzDragThrottleTimer) {
    clearTimeout(ptzDragThrottleTimer);
    ptzDragThrottleTimer = null;
  }
  if (isPtzMode.value) {
    // Switching to PTZ mouse — reset any digital zoom
    zoomPan.resetZoom();
  } else {
    // Leaving PTZ: make sure the camera is not left moving
    sendPTZStop();
  }
}

// -- Stream health --
async function pollStreamHealth() {
  if (!camera.value) return;
  try {
    const res = await api.get(`/cameras/${camera.value.cameraId}/go2rtc/status`);
    const data = res.data || res;
    if (!data.registered) {
      streamHealth.value = 'no-producer';
    } else if (data.producers && data.producers.length > 0) {
      streamHealth.value = 'ok';
    } else {
      streamHealth.value = 'no-producer';
    }
  } catch {
    streamHealth.value = 'error';
  }
}

async function reconnectStream() {
  if (!camera.value) return;
  streamHealth.value = 'unknown';
  try {
    await api.post(`/cameras/${camera.value.cameraId}/go2rtc/reconnect`);
    await new Promise((r) => {
      setTimeout(r, 800);
    });
    webrtcPlayer.value?.reconnect();
    pollStreamHealth();
  } catch (err) {
    console.error('[CameraModal] Stream reconnect failed:', err);
    streamHealth.value = 'error';
  }
}

// -- Mouse PTZ (drag-to-pan + click-to-center) --

function onVideoMouseDown(e: MouseEvent) {
  if (e.button !== 0) return; // left button only
  e.preventDefault();

  if (cameraHasPtz.value && isPtzMode.value) {
    // PTZ drag/click-to-center
    ptzDragStart.value = { x: e.clientX, y: e.clientY, clientX: e.clientX, clientY: e.clientY };
    ptzDragging.value = false;
    window.addEventListener('mousemove', onPtzMouseMove);
    window.addEventListener('mouseup', onPtzMouseUp);
  } else if (isDigitalZoomMode.value && zoomPan.scale.value > 1) {
    // Digital zoom pan — drag to pan the zoomed image
    const startOffsetX = zoomPan.offsetX.value;
    const startOffsetY = zoomPan.offsetY.value;
    const startX = e.clientX;
    const startY = e.clientY;

    const onMove = (ev: MouseEvent) => {
      zoomPan.setZoom(zoomPan.scale.value, startOffsetX + (ev.clientX - startX), startOffsetY + (ev.clientY - startY));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }
}

function onPtzMouseMove(e: MouseEvent) {
  if (!ptzDragStart.value || !videoWrapperRef.value) return;
  const dx = e.clientX - ptzDragStart.value.x;
  const dy = e.clientY - ptzDragStart.value.y;

  if (!ptzDragging.value && !isTap(dx, dy)) {
    ptzDragging.value = true;
  }

  if (!ptzDragging.value) return;

  // Throttle API calls at 80ms
  if (ptzDragThrottleTimer) return;
  ptzDragThrottleTimer = setTimeout(() => {
    ptzDragThrottleTimer = null;
  }, 80);

  sendPtzDragSample(e.clientX, e.clientY, ptzDragStart.value);
}

/**
 * Push one drag sample to the camera. The anchor advances only when a sample is
 * actually sent, so movement during a throttle window is captured rather than
 * dropped — shared by the mouse drag and the one-finger touch drag.
 */
function sendPtzDragSample(clientX: number, clientY: number, anchor: { clientX: number; clientY: number }) {
  if (!videoWrapperRef.value) return;
  const moveDx = clientX - anchor.clientX;
  const moveDy = clientY - anchor.clientY;
  anchor.clientX = clientX;
  anchor.clientY = clientY;

  const rect = videoWrapperRef.value.getBoundingClientRect();
  const velocity = ptzPanVelocity(moveDx, moveDy, rect.width, rect.height);
  sendPTZMove({ x: velocity.x, y: velocity.y, zoom: 0 });
}

function onPtzMouseUp(e: MouseEvent) {
  removePtzMouseListeners();
  if (!ptzDragStart.value || !videoWrapperRef.value) return;

  const dx = e.clientX - ptzDragStart.value.x;
  const dy = e.clientY - ptzDragStart.value.y;

  if (ptzDragging.value) {
    // End of drag — stop movement
    ptzDragging.value = false;
    sendPTZStop();
  } else if (isTap(dx, dy)) {
    // Tiny movement = click-to-center
    sendPTZStop();
    performClickCenter(e.clientX, e.clientY);
  }

  ptzDragStart.value = null;
}

function removePtzMouseListeners() {
  window.removeEventListener('mousemove', onPtzMouseMove);
  window.removeEventListener('mouseup', onPtzMouseUp);
  if (ptzDragThrottleTimer) {
    clearTimeout(ptzDragThrottleTimer);
    ptzDragThrottleTimer = null;
  }
}

// -- Touch gestures (phones and tablets) --
//
// The mouse gets a grab cursor, a drag and a wheel; a finger gets the same
// three gestures: one finger drags (PTZ pan/tilt in PTZ mode, image pan in
// digital zoom), two fingers pinch (PTZ zoom in PTZ mode, digital zoom
// otherwise), a tap centres the camera, a double-tap resets the digital zoom.

/** Throttle window for pinch-driven PTZ zoom commands (ms). */
const PTZ_PINCH_SEND_INTERVAL_MS = 80;

/** Two taps count as a double-tap (reset) when they land within this window (ms). */
const DOUBLE_TAP_WINDOW_MS = 400;

function touchPoint(touch: Touch): { clientX: number; clientY: number } {
  return { clientX: touch.clientX, clientY: touch.clientY };
}

function resetTouchGesture() {
  touchGesture.kind = 'none';
}

/** Start tracking a single remaining finger (after a pinch, or when one of two fingers lifts). */
function beginSingleTouch(touch: Touch) {
  touchGesture.startX = touch.clientX;
  touchGesture.startY = touch.clientY;
  touchGesture.startAt = Date.now();
  ptzDragging.value = false;

  if (cameraHasPtz.value && isPtzMode.value) {
    touchGesture.kind = 'ptz-drag';
    touchGesture.anchor.clientX = touch.clientX;
    touchGesture.anchor.clientY = touch.clientY;
  } else {
    touchGesture.kind = 'digital-drag';
    touchGesture.panStartOffsetX = zoomPan.offsetX.value;
    touchGesture.panStartOffsetY = zoomPan.offsetY.value;
  }
}

function beginPinch(touches: TouchList) {
  const pair = touchPair(touchPoint(touches[0]), touchPoint(touches[1]));
  touchGesture.pinchStartDistance = pair.distance || 1;
  touchGesture.pinchLastRatio = 1;
  touchGesture.pinchStartMidX = pair.midX;
  touchGesture.pinchStartMidY = pair.midY;
  touchGesture.pinchStartScale = zoomPan.scale.value;
  touchGesture.pinchStartOffsetX = zoomPan.offsetX.value;
  touchGesture.pinchStartOffsetY = zoomPan.offsetY.value;
  touchGesture.startAt = Date.now();

  // A drag that turns into a pinch must stop moving the camera.
  const wasDragging = ptzDragging.value;
  if (ptzDragThrottleTimer) {
    clearTimeout(ptzDragThrottleTimer);
    ptzDragThrottleTimer = null;
  }
  ptzDragging.value = false;
  if (wasDragging) sendPTZStop();

  touchGesture.kind = cameraHasPtz.value && isPtzMode.value ? 'ptz-pinch' : 'digital-pinch';
}

function onVideoTouchStart(e: TouchEvent) {
  if (playingRecording.value) return;
  e.preventDefault();

  if (e.touches.length >= 2) {
    beginPinch(e.touches);
    return;
  }

  const touch = e.touches[0];
  if (!touch) return;
  beginSingleTouch(touch);
}

function onVideoTouchMove(e: TouchEvent) {
  if (playingRecording.value) return;
  const kind = touchGesture.kind;
  if (kind === 'none') return;
  e.preventDefault();

  if (kind === 'ptz-pinch') {
    if (e.touches.length < 2) return;
    const pair = touchPair(touchPoint(e.touches[0]), touchPoint(e.touches[1]));
    const ratio = pair.distance / (touchGesture.pinchStartDistance || 1);
    const velocity = ptzZoomVelocity(ratio, touchGesture.pinchLastRatio);
    touchGesture.pinchLastRatio = ratio;

    if (velocity !== 0 && !ptzDragThrottleTimer) {
      ptzDragThrottleTimer = setTimeout(() => {
        ptzDragThrottleTimer = null;
      }, PTZ_PINCH_SEND_INTERVAL_MS);
      sendPTZMove({ x: 0, y: 0, zoom: velocity * 0.8 });
    }
    if (velocity !== 0) {
      // Keep the camera zooming briefly after the fingers stop, exactly like the wheel.
      if (ptzZoomStopTimer) clearTimeout(ptzZoomStopTimer);
      ptzZoomStopTimer = setTimeout(() => {
        sendPTZStop();
        ptzZoomStopTimer = null;
      }, 300);
    }
    return;
  }

  if (kind === 'digital-pinch') {
    if (e.touches.length < 2 || !videoWrapperRef.value) return;
    const pair = touchPair(touchPoint(e.touches[0]), touchPoint(e.touches[1]));
    const rect = videoWrapperRef.value.getBoundingClientRect();
    const next = pinchTransform({
      midX: pair.midX,
      midY: pair.midY,
      rectLeft: rect.left,
      rectTop: rect.top,
      startScale: touchGesture.pinchStartScale,
      startOffsetX: touchGesture.pinchStartOffsetX,
      startOffsetY: touchGesture.pinchStartOffsetY,
      startMidX: touchGesture.pinchStartMidX,
      startMidY: touchGesture.pinchStartMidY,
      startDistance: touchGesture.pinchStartDistance,
      distance: pair.distance,
      minScale: 1,
      maxScale: DIGITAL_ZOOM_MAX,
    });
    zoomPan.setZoom(next.scale, next.offsetX, next.offsetY);
    return;
  }

  const touch = e.touches[0];
  if (!touch) return;
  const dx = touch.clientX - touchGesture.startX;
  const dy = touch.clientY - touchGesture.startY;

  if (kind === 'ptz-drag') {
    if (!ptzDragging.value && !isTap(dx, dy)) {
      ptzDragging.value = true;
    }
    if (!ptzDragging.value) return;
    if (ptzDragThrottleTimer) return;
    ptzDragThrottleTimer = setTimeout(() => {
      ptzDragThrottleTimer = null;
    }, 80);
    sendPtzDragSample(touch.clientX, touch.clientY, touchGesture.anchor);
    return;
  }

  if (kind === 'digital-drag') {
    // Nothing to pan until the image is zoomed in (same rule as the mouse).
    if (zoomPan.scale.value <= 1) return;
    zoomPan.setZoom(zoomPan.scale.value, touchGesture.panStartOffsetX + dx, touchGesture.panStartOffsetY + dy);
  }
}

function onVideoTouchEnd(e: TouchEvent) {
  const kind = touchGesture.kind;
  if (kind === 'none') return;

  if (kind === 'ptz-pinch') {
    if (e.touches.length >= 2) return;
    if (ptzZoomStopTimer) {
      clearTimeout(ptzZoomStopTimer);
      ptzZoomStopTimer = null;
    }
    sendPTZStop();
    if (e.touches.length === 1) beginSingleTouch(e.touches[0]);
    else resetTouchGesture();
    return;
  }

  if (kind === 'digital-pinch') {
    if (e.touches.length >= 2) return;
    if (e.touches.length === 1) beginSingleTouch(e.touches[0]);
    else resetTouchGesture();
    return;
  }

  if (e.touches.length > 0) {
    // One of several fingers lifted mid-gesture — keep tracking the rest.
    beginSingleTouch(e.touches[0]);
    return;
  }

  const ended = e.changedTouches[0];
  const wasTap =
    !!ended &&
    Date.now() - touchGesture.startAt < 500 &&
    isTap(ended.clientX - touchGesture.startX, ended.clientY - touchGesture.startY);

  if (kind === 'ptz-drag') {
    if (ptzDragThrottleTimer) {
      clearTimeout(ptzDragThrottleTimer);
      ptzDragThrottleTimer = null;
    }
    if (ptzDragging.value) {
      ptzDragging.value = false;
      sendPTZStop();
    } else if (wasTap && ended) {
      // A tap centres the camera where it was tapped, like a mouse click.
      sendPTZStop();
      performClickCenter(ended.clientX, ended.clientY);
    }
  } else if (kind === 'digital-drag' && wasTap) {
    const now = Date.now();
    if (now - touchGesture.lastTapAt < DOUBLE_TAP_WINDOW_MS) {
      zoomPan.resetZoom();
      touchGesture.lastTapAt = 0;
    } else {
      touchGesture.lastTapAt = now;
    }
  }

  resetTouchGesture();
}

function onVideoTouchCancel() {
  // The system took the gesture away (notification, call) — stop, never click.
  if (touchGesture.kind === 'ptz-drag' || touchGesture.kind === 'ptz-pinch') {
    if (ptzDragThrottleTimer) {
      clearTimeout(ptzDragThrottleTimer);
      ptzDragThrottleTimer = null;
    }
    if (ptzZoomStopTimer) {
      clearTimeout(ptzZoomStopTimer);
      ptzZoomStopTimer = null;
    }
    ptzDragging.value = false;
    sendPTZStop();
  }
  resetTouchGesture();
}

function performClickCenter(clientX: number, clientY: number) {
  if (!camera.value || !videoWrapperRef.value) return;

  const rect = videoWrapperRef.value.getBoundingClientRect();

  // Raw offset from container center (-0.5..0.5)
  let relX = (clientX - rect.left) / rect.width - 0.5;
  let relY = (clientY - rect.top) / rect.height - 0.5;

  // Letterboxing correction — video uses object-contain so black bars may exist
  const video = webrtcPlayer.value?.$el?.querySelector('video') as HTMLVideoElement | null;
  if (video && video.videoWidth && video.videoHeight) {
    const containerRatio = rect.width / rect.height;
    const videoRatio = video.videoWidth / video.videoHeight;
    if (videoRatio > containerRatio) {
      // Pillarbox top/bottom: scale Y to actual video area
      const activeHeight = rect.width / videoRatio;
      const scaleY = rect.height / activeHeight;
      relY *= scaleY;
    } else {
      // Letterbox left/right: scale X to actual video area
      const activeWidth = rect.height * videoRatio;
      const scaleX = rect.width / activeWidth;
      relX *= scaleX;
    }
  }

  // Convert to -1..1
  const x = Math.max(-1, Math.min(1, relX * 2));
  const y = Math.max(-1, Math.min(1, -relY * 2)); // invert Y: down on screen = tilt down

  // Show crosshair flash at click position
  clickFlash.value = { x: clientX - rect.left, y: clientY - rect.top };
  setTimeout(() => {
    clickFlash.value = null;
  }, 600);

  api
    .post(`/cameras/${camera.value.cameraId}/ptz/click-center`, { x, y })
    .catch((err) => console.error('[CameraModal] click-center PTZ failed:', err));
}

function onVideoWheel(e: WheelEvent) {
  if (!camera.value) return;
  e.preventDefault();

  if (isDigitalZoomMode.value) {
    // Digital zoom toward cursor
    const delta = e.deltaY > 0 ? -0.3 : 0.3;
    zoomPan.zoomToPoint(e.clientX, e.clientY, delta);
    return;
  }

  // Hardware PTZ zoom
  const zoom = e.deltaY > 0 ? -0.8 : 0.8;
  sendPTZMove({ x: 0, y: 0, zoom });
  if (ptzZoomStopTimer) clearTimeout(ptzZoomStopTimer);
  ptzZoomStopTimer = setTimeout(() => {
    sendPTZStop();
    ptzZoomStopTimer = null;
  }, 300);
}

function onVideoDoubleClick() {
  if (isDigitalZoomMode.value) {
    zoomPan.resetZoom();
  }
}

// -- Snapshot --
async function takeSnapshot() {
  if (!camera.value || takingSnapshot.value) return;
  takingSnapshot.value = true;
  try {
    const response = await api.get(`/cameras/${camera.value.cameraId}/snapshot`, {
      responseType: 'blob',
    });
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `snapshot_${camera.value.cameraName || camera.value.cameraId}_${ts}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('[CameraModal] Snapshot failed:', err);
  } finally {
    takingSnapshot.value = false;
  }
}

// -- PTZ --
function handlePTZ(data: { x: number; y: number; zoom: number }) {
  if (!camera.value) return;
  const { x, y, zoom } = data;
  isPTZMoving.value = x !== 0 || y !== 0 || zoom !== 0;

  if (!isPTZMoving.value) {
    if (ptzThrottleTimer) {
      clearTimeout(ptzThrottleTimer);
      ptzThrottleTimer = null;
    }
    ptzNextData = null;
    sendPTZStop();
    return;
  }

  if (ptzThrottleTimer) {
    ptzNextData = data;
  } else {
    sendPTZMove(data);
    startPTZTimer();
  }
}

function startPTZTimer() {
  ptzThrottleTimer = setTimeout(() => {
    if (ptzNextData) {
      sendPTZMove(ptzNextData);
      ptzNextData = null;
      startPTZTimer();
    }
  }, 200);
}

function sendPTZMove(data: PtzMovePayload) {
  if (!camera.value) return;
  api.post(`/cameras/${camera.value.cameraId}/ptz/move`, data).catch((err: unknown) => console.error(err));
}

function sendPTZStop() {
  if (!camera.value) return;
  api.post(`/cameras/${camera.value.cameraId}/ptz/stop`).catch((err: unknown) => console.error(err));
}

// -- Presets --
async function loadPresets() {
  if (!camera.value) return;
  loadingPresets.value = true;
  try {
    const res = await api.get(`/cameras/${camera.value.cameraId}/ptz/presets`);
    presets.value = res.data || res;
    console.log('[CameraModal] Presets loaded:', {
      cameraId: camera.value.cameraId,
      count: presets.value.length,
      presets: presets.value,
    });
  } catch (err) {
    console.error('[CameraModal] Failed to load presets:', err);
    presetError.value = tt('Failed to load presets from camera', 'Prednastavitev s kamere ni bilo mogoče naložiti');
  } finally {
    loadingPresets.value = false;
  }
}

async function savePreset() {
  if (!camera.value || !newPresetName.value.trim()) return;
  savingPreset.value = true;
  presetError.value = null;
  try {
    // Set preset on camera via ONVIF
    const setRes = await api.post(`/cameras/${camera.value.cameraId}/ptz/presets/save`, {
      presetName: newPresetName.value.trim(),
    });
    const presetToken = setRes.data?.presetToken || setRes.presetToken;

    console.log('[CameraModal] Preset saved on camera:', {
      cameraId: camera.value.cameraId,
      presetName: newPresetName.value.trim(),
      presetToken,
    });

    // Save to database
    await api.post(`/cameras/${camera.value.cameraId}/ptz/presets`, {
      presetToken,
      presetName: newPresetName.value.trim(),
      isHome: false,
    });

    console.log('[CameraModal] Preset saved to database');

    newPresetName.value = '';
    showSavePreset.value = false;

    // Reload presets list
    await loadPresets();
    presetError.value = null; // Clear any errors after successful load
    console.log('[CameraModal] Presets reloaded after save');
  } catch (__caught: unknown) {
    const err = __caught as {
      response?: {
        status?: number;
        data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
      };
      message?: string;
    };
    console.error('[CameraModal] Failed to save preset:', err);
    // Handle 409 Conflict (duplicate preset name)
    if (err.response?.status === 409) {
      presetError.value = tt(
        `Preset "${newPresetName.value.trim()}" already exists on this camera. Please use a different name.`,
        `Prednastavitev »${newPresetName.value.trim()}« na tej kameri že obstaja. Uporabite drugo ime.`,
      );
    } else {
      const errorMsg =
        err.response?.data?.details ||
        err.message ||
        tt('Failed to save preset', 'Prednastavitve ni bilo mogoče shraniti');
      presetError.value = errorMsg;
    }
  } finally {
    savingPreset.value = false;
  }
}

async function gotoPreset(preset: PtzPreset) {
  if (!camera.value) return;
  goingToPreset.value = preset.presetToken;
  try {
    await api.post(`/cameras/${camera.value.cameraId}/ptz/preset/${preset.presetToken}`);
  } catch (err) {
    console.error('[CameraModal] Failed to go to preset:', err);
  } finally {
    setTimeout(() => {
      goingToPreset.value = null;
    }, 500);
  }
}

async function deletePreset(preset: PtzPreset) {
  if (!camera.value) return;
  try {
    await api.delete(`/cameras/${camera.value.cameraId}/ptz/presets/${preset.presetToken}`);
    await loadPresets();
  } catch (err) {
    console.error('[CameraModal] Failed to delete preset:', err);
  }
}

// -- Recordings --
async function loadRecordings() {
  if (!camera.value) return;
  loadingRecordings.value = true;
  try {
    const res = await api.get(`/cameras/${camera.value.cameraId}/recordings`, {
      params: {
        startDate: recordingsFilter.value.startDate,
        endDate: recordingsFilter.value.endDate,
        limit: 50,
      },
    });
    clearThumbnailUrls();
    recordings.value = res.data || res;
    thumbnailLoadFailed.value = {};
    await loadRecordingThumbnails(recordings.value);
  } catch (err) {
    console.error('[CameraModal] Failed to load recordings:', err);
    clearThumbnailUrls();
    recordings.value = [];
    thumbnailLoadFailed.value = {};
  } finally {
    loadingRecordings.value = false;
  }
}

function getRecordingKey(recording: CameraRecording): string {
  return `${recording.recordingEventId}_${recording.segmentNumber}`;
}

function getRecordingThumbnailEndpoint(recording: CameraRecording): string {
  const endpoint = `/cameras/recordings/${recording.cameraId}/${recording.recordingEventId}/${recording.segmentNumber}/thumbnail`;
  return authStore.token ? `${endpoint}?token=${encodeURIComponent(authStore.token)}` : endpoint;
}

function clearThumbnailUrls() {
  Object.values(thumbnailUrls.value).forEach((url) => {
    URL.revokeObjectURL(url);
  });
  thumbnailUrls.value = {};
  thumbnailLoading.value = {};
}

async function loadRecordingThumbnails(recordingList: CameraRecording[]) {
  await Promise.allSettled(recordingList.map((recording) => loadThumbnail(recording)));
}

async function loadThumbnail(recording: CameraRecording) {
  const key = getRecordingKey(recording);
  thumbnailLoading.value = {
    ...thumbnailLoading.value,
    [key]: true,
  };

  try {
    const res = await api.get(getRecordingThumbnailEndpoint(recording), { responseType: 'blob' });
    const blob = res.data;
    const objectUrl = URL.createObjectURL(blob);
    thumbnailUrls.value = {
      ...thumbnailUrls.value,
      [key]: objectUrl,
    };
    thumbnailLoadFailed.value = {
      ...thumbnailLoadFailed.value,
      [key]: false,
    };
  } catch {
    thumbnailLoadFailed.value = {
      ...thumbnailLoadFailed.value,
      [key]: true,
    };
  } finally {
    thumbnailLoading.value = {
      ...thumbnailLoading.value,
      [key]: false,
    };
  }
}

async function playRecording(recording: CameraRecording) {
  if (!camera.value) return;
  try {
    const res = await api.get(
      `/cameras/recordings/${recording.cameraId}/${recording.recordingEventId}/${recording.segmentNumber}/stream`,
    );
    const data = res.data || res;
    recordingVideoUrl.value = data.url;
    playingRecordingInfo.value = recording.startTime
      ? `${formatDateTime(recording.startTime)}${recording.durationSeconds ? ' · ' + formatDuration(recording.durationSeconds) : ''}`
      : '';
    playingRecording.value = true;
  } catch (err) {
    console.error('[CameraModal] Failed to get recording stream URL:', err);
  }
}

function stopPlayback() {
  playingRecording.value = false;
  recordingVideoUrl.value = '';
  playingRecordingInfo.value = '';
}

async function downloadRecording(recording: CameraRecording) {
  if (!camera.value) return;
  try {
    const res = await api.get(
      `/cameras/recordings/${recording.cameraId}/${recording.recordingEventId}/${recording.segmentNumber}/stream`,
    );
    const data = res.data || res;
    const a = document.createElement('a');
    a.href = data.url;
    a.download = `recording_${recording.recordingEventId}_seg${recording.segmentNumber}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error('[CameraModal] Failed to get download URL:', err);
  }
}

async function deleteRecording(recording: CameraRecording) {
  if (!camera.value) return;
  const ok = await confirmBox(t('dialogs.cameraDeleteRecording'));
  if (!ok) return;
  try {
    await api.delete(`/cameras/recordings/${camera.value.cameraId}/${recording.recordingEventId}`);
    await loadRecordings();
  } catch (err) {
    console.error('[CameraModal] Failed to delete recording:', err);
  }
}

// -- Motion Events --
let motionCleanup: (() => void) | null = null;

function setupMotionListener() {
  if (!socketStore.socket || !camera.value) return;

  const handler = (payload: { deviceId?: string; valueKey?: string; value?: unknown }) => {
    if (!camera.value) return;
    // Match by deviceId (the device_value_changed payload uses deviceId + valueKey)
    const devId = cameraDeviceId.value;
    if (!devId) return;
    if (payload.deviceId !== devId) return;
    console.log('[CameraModal] device_value_changed for camera:', {
      cameraId: camera.value.cameraId,
      valueKey: payload.valueKey,
      value: payload.value,
    });
    if (payload.valueKey === 'motion_detected') {
      const active = payload.value === true || payload.value === 'true';
      motionActive.value = active;
      motionEvents.value.unshift({
        id: `${Date.now()}_${Math.random()}`,
        active,
        timestamp: new Date(),
      });
      // Keep last 50 events
      if (motionEvents.value.length > 50) {
        motionEvents.value = motionEvents.value.slice(0, 50);
      }
    }
  };

  socketStore.socket.on('device_value_changed', handler);
  motionCleanup = () => {
    socketStore.socket?.off('device_value_changed', handler);
  };
}

function cleanupMotionListener() {
  motionCleanup?.();
  motionCleanup = null;
}

// -- Formatting --
function formatDateTime(dateStr: string): string {
  return formatDateTimeLocale(dateStr);
}

function formatDuration(seconds: number): string {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatTime(date: Date): string {
  return formatTimeLocale(date);
}

// -- Cleanup --
onUnmounted(() => {
  if (ptzThrottleTimer) clearTimeout(ptzThrottleTimer);
  if (ptzZoomStopTimer) clearTimeout(ptzZoomStopTimer);
  if (ptzDragThrottleTimer) clearTimeout(ptzDragThrottleTimer);
  if (streamHealthInterval) clearInterval(streamHealthInterval);
  removePtzMouseListeners();
  clearThumbnailUrls();
  cleanupMotionListener();
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

/* Click-to-center crosshair flash transition */
.click-flash-enter-active {
  transition: opacity 0.1s;
}
.click-flash-leave-active {
  transition: opacity 0.5s;
}
.click-flash-enter-from {
  opacity: 0;
}
.click-flash-leave-to {
  opacity: 0;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
.animate-pulse-slow {
  animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.1;
  }
}
</style>
